/**
 * Order a campaign (client kickoff): the brand orders a campaign itself, as one flow. Lykke on the left (chat, call,
 * files), the order on the right as a document prefilled from the Brand OS, a progress meter, and "Order campaign"
 * with a verifier. Ordering always goes the Autonomous route: agents take the repetitive steps, the brand holds the
 * gates. Backend: cowork /v1/orders (routes/clientOrders.ts). The back office keeps its own kickoff studio.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Loader2, MessageCircle, Plus } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';
import { ordersApi, money, type Block, type ClientOrder, type OrderShape, type Value } from './api';
import { FieldRow, Choice } from './Fields';
import { Blocks } from './Blocks';
import { LykkePanel } from './LykkePanel';
import { OrderConfirm, OrderDone } from './OrderConfirm';
import { anchorOf, fieldShown, progressOf, scrollToInDoc } from './model';
import { AgentPanelSlot, useInShell } from '@/cowork/shell/ShellContext';
import { usePageContext } from '@/cowork/shell/pageContext';

export function ClientKickoffPage() {
  const { brandId, brandName } = useEffectiveBrand();
  const location = useLocation(); const navigate = useNavigate();
  const orderId = new URLSearchParams(location.search).get('order');
  const [startError, setStartError] = useState<string | null>(null);
  const starting = useRef(false);

  const open = useCallback((id: string) => navigate(`/campaigns/new?order=${encodeURIComponent(id)}`, { replace: true }), [navigate]);
  const startNew = useCallback(async (fresh: boolean) => {
    if (!brandId || starting.current) return;
    starting.current = true; setStartError(null);
    try {
      if (!fresh) {
        const { orders } = await ordersApi.list(brandId);
        const draft = orders.filter((o) => o.status === 'draft').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
        if (draft) { open(draft.id); return; }
      }
      const r = await ordersApi.create(brandId, brandName);
      open(r.order.id);
    } catch (e) { setStartError((e as Error).message); }
    finally { starting.current = false; }
  }, [brandId, brandName, open]);
  useEffect(() => { if (!orderId && brandId) void startNew(false); }, [orderId, brandId, startNew]);

  const q = useQuery({ queryKey: ['client-order', orderId], queryFn: () => ordersApi.get(orderId!), enabled: !!orderId, retry: false });
  if (!brandId) return <Centered>Pick a brand in the top bar to order a campaign.</Centered>;
  if (startError) return <Centered>{startError}</Centered>;
  if (!orderId || q.isLoading) return <Centered><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Reading your Brand OS</Centered>;
  if (q.error || !q.data) return <Centered>{(q.error as Error)?.message || 'This order could not be opened.'}</Centered>;
  return <Studio key={orderId} shape={q.data} onNew={() => void startNew(true)} />;
}
const Centered = ({ children }: { children: React.ReactNode }) => <div className="grid min-h-[60vh] place-items-center px-4 text-center text-sm text-[#667085]"><p className="max-w-md">{children}</p></div>;

function Studio({ shape, onNew }: { shape: OrderShape; onNew: () => void }) {
  const qc = useQueryClient(); const navigate = useNavigate();
  const [order, setOrderState] = useState<ClientOrder>(shape.order);
  const setOrder = useCallback((o: ClientOrder) => { setOrderState(o); qc.setQueryData(['client-order', o.id], (old: OrderShape | undefined) => (old ? { ...old, order: o } : old)); }, [qc]);
  const [saving, setSaving] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showMissing, setShowMissing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const readOnly = order.status !== 'draft';
  // New layout: the order's conversation is Lykke's panel in "Order" mode, the document takes the workspace.
  const inShell = useInShell();
  usePageContext({ page: 'campaign-new', label: 'Order a campaign', title: order.brandName ? `${order.brandName} order` : undefined, objects: { orderId: order.id } });

  const save = useCallback(async (patch: { values?: Record<string, Value>; blocks?: Block[]; sizeChoice?: string }) => {
    setSaving((n) => n + 1); setSaveError(null);
    try { const r = await ordersApi.save(order.id, patch); setOrder(r.order); }
    catch (e) { setSaveError((e as Error).message); }
    finally { setSaving((n) => n - 1); }
  }, [order.id, setOrder]);

  const progress = useMemo(() => progressOf(shape.sections, order), [shape.sections, order]);
  const gapIds = useMemo(() => new Set(order.gaps.map((g) => g.id)), [order.gaps]);
  const missingKeys = useMemo(() => new Set(order.gaps.filter((g) => g.id.startsWith('block:')).map((g) => g.id.slice(6))), [order.gaps]);
  const currency = String(order.values.currency || order.total?.currency || 'SEK');
  const hasPosts = order.blocks.some((b) => b.kind === 'posts');
  const hasUgc = order.blocks.some((b) => b.kind === 'ugc');
  const jump = (sectionOrGap: string) => scrollToInDoc(sectionOrGap.startsWith('ck-') ? sectionOrGap : anchorOf(sectionOrGap));
  const tryOrder = () => {
    if (order.gaps.length) { setShowMissing(true); jump(order.gaps[0].id); return; }
    setConfirm(true);
  };

  if (readOnly) return <OrderDone order={order} onCampaigns={() => navigate('/campaigns')} onCampaign={(cid) => navigate(`/campaigns/${encodeURIComponent(cid)}`)} />;

  return (
    <div className="flex flex-col lg:h-[var(--ws-h,calc(100vh-3.5rem))] lg:flex-row lg:overflow-clip">
      {inShell ? (
        <AgentPanelSlot id="kickoff" label="Order" line="Writing your order with you">
          <LykkePanel order={order} readOnly={readOnly} onOrder={setOrder} inPanel />
        </AgentPanelSlot>
      ) : (
        <>
          {/* Lykke: a column on desktop, a panel you open on the phone. */}
          <button type="button" onClick={() => setChatOpen((v) => !v)} className="flex items-center gap-2 border-b border-gray-100 bg-white px-4 py-3 text-left lg:hidden" aria-expanded={chatOpen}>
            <MessageCircle className="h-4 w-4 text-[#be43ff]" aria-hidden /><span className="flex-1 text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>Lykke, your account manager</span>
            <ChevronDown className={cn('h-4 w-4 text-[#98a2b3] transition', chatOpen && 'rotate-180')} aria-hidden />
          </button>
          <LykkePanel order={order} readOnly={readOnly} onOrder={setOrder} className={cn('border-gray-100 lg:flex lg:h-full lg:w-[380px] lg:flex-none lg:border-r', chatOpen ? 'flex h-[60vh] border-b' : 'hidden')} />
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col bg-[#f8f9fa] lg:h-full">
        {/* Progress: what is ready to order, one segment per section. */}
        <div className="z-20 border-b border-gray-100 bg-white/95 max-lg:sticky max-lg:top-0 px-4 py-3 backdrop-blur sm:px-8">
          <div className="mx-auto flex max-w-[880px] flex-col gap-2">
            <div className="flex items-center gap-3">
              <span className={cn('text-[13px] tabular-nums', progress.open ? 'text-[#101828]' : 'text-[#067647]')} style={{ fontWeight: 700 }}>{progress.percent}%</span>
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-[#667085]">{progress.open ? `${progress.open} thing${progress.open === 1 ? '' : 's'} left before you can order` : 'Everything needed is in: you can order'}</span>
              <span className="text-[12px] text-[#98a2b3]" aria-live="polite">{saving ? 'Saving' : saveError ? <span className="text-[#b42318]">{saveError}</span> : <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" aria-hidden />Saved</span>}</span>
            </div>
            <div className="flex gap-1" role="list" aria-label="Sections">
              {progress.sections.map((s) => (
                <button key={s.id} type="button" role="listitem" onClick={() => jump(`ck-section-${s.id}`)} title={`${s.title}: ${s.open ? `${s.open} left` : 'done'}`} className="group flex flex-1 flex-col gap-1 text-left">
                  <span className={cn('h-1.5 rounded-full transition-colors', s.open ? 'bg-[#e9d7fe] group-hover:bg-[#d6bbfb]' : 'bg-[#be43ff]')} />
                  <span className="hidden truncate text-[11px] text-[#667085] xl:block">{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <main className="relative min-w-0 flex-1 px-4 pb-10 pt-6 sm:px-8 lg:overflow-y-auto" id="ck-doc">
          <div className="mx-auto flex max-w-[880px] flex-col gap-5">
            <header className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.08em] text-[#be43ff]" style={{ fontWeight: 600 }}>New campaign · {order.brandName}</div>
                <h1 className="mt-1 text-[26px] leading-tight text-[#101828] sm:text-[30px]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>Order a campaign</h1>
                <p className="mt-1 max-w-[620px] text-[14px] text-[#475467]">Tell us what you want. We filled in what your Brand OS knows: check it, change what is wrong. Agents take the repetitive steps; you approve the creators, the content and the creator payments.</p>
              </div>
              <button type="button" onClick={onNew} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3.5 text-[13px] text-[#344054] hover:bg-gray-50" style={{ fontWeight: 600 }}><Plus className="h-3.5 w-3.5" aria-hidden />New order</button>
            </header>

            {shape.sections.map((s) => (
              <section key={s.id} id={`ck-section-${s.id}`} aria-labelledby={`ck-h-${s.id}`} className="scroll-mt-28 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm sm:p-7">
                <div className="mb-5">
                  <h2 id={`ck-h-${s.id}`} className="text-[18px] text-[#101828]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>{s.title}</h2>
                  <p className="mt-0.5 text-[13px] text-[#667085]">{s.line}</p>
                </div>
                <div className="flex flex-col gap-5">
                  {s.id === 'offering' && (
                    <div id="ck-field-blocks" className={cn('scroll-mt-28 rounded-xl', showMissing && gapIds.has('blocks') && 'ring-2 ring-[#fda29b] ring-offset-4 ring-offset-white')}>
                      <Blocks blocks={order.blocks} kinds={shape.kinds} bands={shape.bands} currency={currency} missingKeys={showMissing ? missingKeys : new Set()} onChange={(blocks) => void save({ blocks })} />
                    </div>
                  )}
                  {s.id === 'creators' && hasUgc && !hasPosts && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Creator size</span>
                      <Choice label="Creator size" value={order.sizeChoice} onChange={(v) => void save({ sizeChoice: v })} options={[{ id: 'any', label: 'Any size' }, ...shape.bands]} />
                      <span className="text-[12px] text-[#667085]">UGC creators make content for your channels, so their own following matters less.</span>
                    </div>
                  )}
                  <div className="grid gap-5 sm:grid-cols-2">
                    {s.fields.filter((f) => fieldShown(f, order.blocks)).map((f) => (
                      <div key={f.id} className={cn(f.kind === 'long' || f.kind === 'chips' || f.kind === 'countries' || f.kind === 'languages' || (f.kind === 'select' && (f.options?.length || 0) > 3) ? 'sm:col-span-2' : '')}>
                        <FieldRow field={f} value={order.values[f.id]} source={order.sources[f.id]} missing={showMissing && gapIds.has(f.id)} products={shape.products}
                          onSave={(v) => void save({ values: { [f.id]: v } })} />
                      </div>
                    ))}
                  </div>
                  {s.id === 'budget' && (
                    <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-2xl bg-[#2e024c] px-5 py-4 text-white">
                      <div><div className="text-[12px] text-white/70">Your total</div><div className="text-[24px] tabular-nums" style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>{order.total ? money(order.total.amount, order.total.currency) : 'Add what you order'}</div></div>
                      <div className="max-w-[340px] text-[12.5px] text-white/75">Excl. VAT, for the creators, their content and Beatly's service. Invoice to follow from Beatly.</div>
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        </main>

        <footer className="z-20 flex max-lg:sticky max-lg:bottom-0 items-center gap-3 border-t border-gray-100 bg-white px-4 py-3 sm:px-8">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] tabular-nums text-[#101828]" style={{ fontWeight: 700 }}>{order.total ? money(order.total.amount, order.total.currency) : 'Nothing ordered yet'}</div>
            <div className="truncate text-[12px] text-[#667085]">{order.total ? 'excl. VAT · invoice to follow' : 'Add what you order to see your total'}</div>
          </div>
          <button type="button" onClick={tryOrder} className={cn('inline-flex h-11 items-center gap-2 rounded-xl px-6 text-[14px] text-white transition active:scale-[0.98]', order.gaps.length ? 'bg-[#d6bbfb] hover:bg-[#c99cf9]' : 'bg-[#be43ff] shadow-[0_4px_14px_rgba(190,67,255,0.35)] hover:bg-[#a830e0]')} style={{ fontWeight: 600 }}>
            Order campaign
          </button>
        </footer>
      </div>

      {confirm && <OrderConfirm order={order} onCancel={() => setConfirm(false)} onOrdered={(r) => { setConfirm(false); setOrder(r.order); void qc.invalidateQueries({ queryKey: ['client-orders'] }); }} />}
    </div>
  );
}
